import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Joystick, Search, Flame, Sparkles, Award, Wand2, Star } from "lucide-react";
import api from "@/lib/api";
import { PlayCard } from "@/components/PlayCard";
import { Spinner, EmptyState } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const CATS = ["All", "Platformer", "Racing", "Puzzle", "Shooter", "Adventure", "Quiz", "Educational", "Card", "Reaction", "Kids"];
const SORTS = [["trending", "Most Popular", Flame], ["new", "Newest", Sparkles], ["top", "Top Rated", Award]];
const DIFFS = ["All", "easy", "medium", "hard"];

export default function PlayGames() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState(params.get("genre") || "All");
  const [sort, setSort] = useState("trending");
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState(params.get("multiplayer") ? "multiplayer" : "all"); // all | multiplayer | single
  const [price, setPrice] = useState(params.get("price") || "all"); // all | free | premium
  const [diff, setDiff] = useState("All");
  const [minRating, setMinRating] = useState(0);
  const [featured, setFeatured] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [event, setEvent] = useState(null);
  const [dares, setDares] = useState([]);
  const [totw, setTotw] = useState(null);
  const [crown, setCrown] = useState([]);

  useEffect(() => {
    api.get("/sandbox/featured").then((r) => setFeatured(r.data?.build || null)).catch(() => {});
    api.get("/arcade/trending-weekly", { params: { limit: 12 } }).then((r) => setWeekly(r.data?.games || [])).catch(() => {});
    api.get("/events/active").then((r) => { if (r.data?.active) setEvent(r.data); }).catch(() => {});
    api.get("/dares/leaderboard", { params: { limit: 8 } }).then((r) => setDares(r.data || [])).catch(() => {});
    api.get("/dares/taunt-of-the-week").then((r) => setTotw(r.data || null)).catch(() => {});
    api.get("/dares/crown-history", { params: { limit: 12 } }).then((r) => setCrown(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = { sort, limit: 60 };
    if (cat !== "All") p.category = cat;
    if (search) p.search = search;
    const t = setTimeout(() => {
      api.get("/arcade", { params: p }).then((r) => { setGames(r.data); setLoading(false); }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [cat, sort, search]);

  const filtered = useMemo(() => games.filter((g) => {
    if (mode === "multiplayer" && !g.multiplayer) return false;
    if (mode === "single" && g.multiplayer) return false;
    if (price === "free" && g.has_premium) return false;
    if (price === "premium" && !g.has_premium) return false;
    if (diff !== "All" && g.difficulty !== diff) return false;
    if (minRating && (g.rating || 0) < minRating) return false;
    return true;
  }), [games, mode, price, diff, minRating]);

  const play = (g) => navigate(`/app/arcade/${g.id}`);
  const chip = (active) => `px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${active ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--border))]"}`;

  return (
    <div className="p-5 lg:p-8 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Joystick className="w-8 h-8 text-[hsl(var(--primary))]" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Play Games</h1>
      </div>
      <p className="text-[hsl(var(--muted-foreground))] mb-5">Instantly play games created by the GameView Studio community.</p>

      {/* Seasonal World Event — a limited-time theme washing across the arcade */}
      {event && (
        <div
          className="relative overflow-hidden rounded-2xl border p-5 mb-6 flex items-center gap-4"
          data-testid="seasonal-event-banner"
          style={{ borderColor: `${event.accent}66`, background: `linear-gradient(110deg, ${event.accent}22, ${event.accent}08)` }}
        >
          <span className="shrink-0 text-4xl sm:text-5xl drop-shadow" data-testid="seasonal-event-emoji">{event.emoji}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-lg sm:text-xl font-extrabold" style={{ color: event.accent }} data-testid="seasonal-event-name">{event.name}</h2>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${event.accent}33`, color: event.accent }}>Live event</span>
              {event.ends_at && (() => {
                const d = Math.max(0, Math.ceil((new Date(event.ends_at) - Date.now()) / 86400000));
                return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border" style={{ borderColor: `${event.accent}66`, color: event.accent }} data-testid="seasonal-event-countdown">⏳ Ends in {d} day{d === 1 ? "" : "s"}</span>;
              })()}
            </div>
            <p className="text-sm text-[hsl(var(--foreground))]/80 mt-0.5">{event.tagline}</p>
          </div>
        </div>
      )}

      <div className="sticky top-16 z-20 -mx-5 px-5 py-3 bg-[hsl(var(--background))]/85 backdrop-blur-xl space-y-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search games…" className="pl-9" data-testid="play-search" />
          </div>
          <div className="flex gap-2">
            {SORTS.map(([v, l, Icon]) => (
              <button key={v} onClick={() => setSort(v)} data-testid={`play-sort-${v}`} className={chip(sort === v) + " flex items-center gap-1.5"}>
                <Icon className="w-4 h-4" /> {l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {CATS.map((c) => <button key={c} onClick={() => setCat(c)} data-testid={`play-cat-${c}`} className={chip(cat === c)}>{c}</button>)}
        </div>
        <div className="flex flex-wrap gap-2 items-center" data-testid="play-filters">
          <button onClick={() => setMode(mode === "multiplayer" ? "all" : "multiplayer")} data-testid="filter-multiplayer" className={chip(mode === "multiplayer")}>👾 Multiplayer</button>
          <button onClick={() => setMode(mode === "single" ? "all" : "single")} data-testid="filter-single" className={chip(mode === "single")}>🎯 Single Player</button>
          <button onClick={() => setPrice(price === "free" ? "all" : "free")} data-testid="filter-free" className={chip(price === "free")}>🆓 Free</button>
          <button onClick={() => setPrice(price === "premium" ? "all" : "premium")} data-testid="filter-premium" className={chip(price === "premium")}>💎 Premium</button>
          <select value={diff} onChange={(e) => setDiff(e.target.value)} data-testid="filter-difficulty" className="rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] px-3 py-1.5 text-sm font-semibold capitalize">
            {DIFFS.map((d) => <option key={d} value={d}>{d === "All" ? "Any difficulty" : d}</option>)}
          </select>
          <select value={minRating} onChange={(e) => setMinRating(Number(e.target.value))} data-testid="filter-rating" className="rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] px-3 py-1.5 text-sm font-semibold">
            <option value={0}>Any rating</option>
            <option value={3}>3★ +</option>
            <option value={4}>4★ +</option>
          </select>
        </div>
      </div>

      <div className="mt-6">
        {!loading && games.length < 8 && (
          <div className="mb-6 rounded-2xl p-6 bg-gradient-to-br from-[hsl(var(--primary))]/15 to-fuchsia-500/10 border border-[hsl(var(--primary))]/30 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="arcade-welcome-hero">
            <div className="text-4xl" aria-hidden>🎮</div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold">The arcade is just getting started — make your mark!</h2>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Describe any idea and our AI builds a playable game in seconds. Be one of the first creators everyone plays.</p>
            </div>
            <Button className="rounded-full shrink-0" onClick={() => navigate("/app/create")} data-testid="arcade-welcome-create">
              <Wand2 className="w-4 h-4" /> Create your first game — free
            </Button>
          </div>
        )}
        {featured && (
          <button onClick={() => navigate(`/app/arcade/${featured.game_id}?build=${featured.id}`)} data-testid="arcade-featured-build"
            className="w-full mb-6 text-left rounded-2xl p-4 bg-gradient-to-br from-amber-500/10 to-[hsl(var(--primary))]/10 border border-amber-500/30 hover:border-amber-500/60 transition-all flex items-center gap-3">
            <Star className="w-5 h-5 text-amber-500 fill-amber-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold text-amber-500">Community creation of the week</div>
              <div className="font-display font-bold truncate">{featured.name} <span className="font-normal text-sm text-[hsl(var(--muted-foreground))]">by {featured.owner_name}</span></div>
            </div>
            <div className="text-sm text-[hsl(var(--muted-foreground))] whitespace-nowrap">🧱 {featured.block_count} · ❤️ {featured.likes || 0} →</div>
          </button>
        )}
        {!search && weekly.length > 0 && (
          <div className="mb-8" data-testid="trending-weekly-rail">
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-5 h-5 text-orange-500" />
              <h2 className="font-display text-lg font-bold">Trending this week</h2>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">— hottest games right now</span>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
              {weekly.map((g, i) => (
                <div key={g.id} className="relative w-[240px] shrink-0 snap-start" data-testid={`trending-weekly-${g.id}`}>
                  <span className="absolute -top-2 -left-2 z-10 w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-black grid place-items-center shadow-lg">#{i + 1}</span>
                  <PlayCard game={g} onPlay={play} />
                </div>
              ))}
            </div>
          </div>
        )}
        {!search && totw && (
          <div className="mb-6" data-testid="taunt-of-the-week">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🏆</span>
              <h2 className="font-display text-lg font-bold">Taunt of the Week</h2>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">— the most-reacted trash-talk across every dare</span>
            </div>
            <button
              onClick={() => navigate(`/app/arcade/${totw.game_id}?challenge=${totw.code}`)}
              data-testid="taunt-of-the-week-card"
              className="w-full text-left rounded-2xl border border-[#f5b301]/40 bg-gradient-to-r from-[#f5b301]/15 via-[#ff7a59]/10 to-transparent hover:border-[#f5b301]/70 p-4 transition-all flex items-center gap-4"
            >
              <span className="shrink-0 text-3xl">🔥</span>
              <span className="min-w-0 flex-1">
                <span className="block text-base font-bold truncate">“{totw.text}”</span>
                <span className="block text-xs text-[hsl(var(--muted-foreground))] truncate">— {totw.user_name} · on Wave {totw.wave} of {totw.game_title}</span>
              </span>
              <span className="shrink-0 text-sm font-bold text-[#f5b301] whitespace-nowrap">🔥 {totw.reactions ?? 0} · Take it →</span>
            </button>
          </div>
        )}
        {!search && crown.length > 0 && (
          <div className="mb-6" data-testid="crown-history">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">👑</span>
              <h2 className="font-display text-lg font-bold">Hall of Champions</h2>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">— past Taunt of the Week winners</span>
            </div>
            <div className="grid gap-2">
              {crown.map((c) => (
                <button
                  key={c.week}
                  onClick={() => c.game_id && navigate(`/app/arcade/${c.game_id}?challenge=${c.code}`)}
                  data-testid={`crown-history-${c.week}`}
                  className="w-full text-left rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:border-[#f5b301]/60 p-3 transition-colors flex items-center gap-3"
                >
                  <span className="shrink-0 text-xs font-mono text-[hsl(var(--muted-foreground))] w-20">{c.week}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold truncate">👑 {c.user_name}</span>
                    <span className="block text-xs text-[hsl(var(--muted-foreground))] truncate">“{c.text}”</span>
                  </span>
                  <span className="shrink-0 text-xs font-bold text-[#f5b301] whitespace-nowrap">🔥 {c.reactions ?? 0}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {!search && dares.length > 0 && (
          <div className="mb-8" data-testid="hall-of-dares">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🔥</span>
              <h2 className="font-display text-lg font-bold">Hall of Dares</h2>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">— the toughest Boss Rush challenges. Can you beat them?</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {dares.map((d, i) => (
                <button
                  key={d.code}
                  onClick={() => navigate(`/app/arcade/${d.game_id}?challenge=${d.code}`)}
                  data-testid={`hall-dare-${d.code}`}
                  className="flex items-center gap-3 text-left rounded-xl border border-[#f5b301]/30 bg-gradient-to-r from-[#f5b301]/10 to-transparent hover:border-[#f5b301]/60 p-3 transition-all"
                >
                  <span className="shrink-0 w-8 h-8 rounded-full bg-[#f5b301]/20 text-[#f5b301] font-black grid place-items-center text-sm">#{i + 1}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold truncate">Wave {d.wave} · {d.game_title}</span>
                    <span className="block text-xs text-[hsl(var(--muted-foreground))] truncate">by {d.challenger_name} · beaten {d.beaten}×{d.message ? ` · “${d.message}”` : ""}</span>
                  </span>
                  {d.comment_count > 0 && (
                    <span className="shrink-0 text-xs font-semibold text-[#f5b301] whitespace-nowrap" data-testid={`hall-dare-comments-${d.code}`} title={`${d.comment_count} taunts & tips`}>💬 {d.comment_count}</span>
                  )}
                  <span className="text-xs font-bold text-[#f5b301] whitespace-nowrap">Take it →</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <h2 className="font-display text-lg font-bold mb-3">{filtered.length} game{filtered.length === 1 ? "" : "s"}</h2>
        {loading ? <Spinner /> : filtered.length === 0 ? (
          <EmptyState emoji="🕹️" title="No games match" text="Try clearing some filters or searching something else." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="play-grid">
            {filtered.map((g) => <PlayCard key={g.id} game={g} onPlay={play} />)}
          </div>
        )}
      </div>
    </div>
  );
}
